import type { BaseEvent } from '@/data/models/events/_BaseEvent';
import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';
import { toTripLegStatusBC } from '@/data/models/trip-leg-status.model';
import { TripLegEventType } from '@/data/events/trip-leg';

// Realtime rules for Monitoring > Today's Trips.
//
// Every trip leg event already updates the shared trip leg entity in place
// (see updateTripLeg.ts and trip-leg.slice.ts), and the page's selector maps
// those entities onto the page's server-sorted list of reference IDs. So a
// refetch is only needed when the server's membership or order of the page
// being viewed may have changed, and even then it only needs the reference
// IDs and pagination (an IDs-only sync), not the trip leg data itself. An
// IDs-only sync that returns the same page changes nothing on screen.
//
// Events that can't affect the page being viewed never trigger a sync, so
// activity elsewhere doesn't refetch or disturb the user's current view.

// The exception events this page always re-syncs for when the trip leg is
// visible, so its row lands where the server now sorts it. Exception state
// isn't a filter or sort column on this page, so these can't move a trip leg
// from another page onto this one.
const EXCEPTION_EVENT_TYPES: string[] = [
  TripLegEventType.TripLegException,
  TripLegEventType.TripLegExceptionAssigned,
  TripLegEventType.TripLegExceptionCleared,
];

// Events for a newly created trip leg, with the status each one implies when
// the payload doesn't carry it
const CREATED_EVENT_STATUS_BCS: Record<string, string> = {
  [TripLegEventType.TripLegDrafted]: 'DRAFT',
  [TripLegEventType.TripLegOpened]: 'OPEN',
};

// Some sortable columns are display names the table looks up from a code on
// the trip leg (see MonitorTripsTable.tsx), and the server sorts on what's
// behind them, so a change to that code is what moves the row. Columns not
// listed here are fields on the trip leg itself.
const SORT_COLUMN_FIELDS: Record<string, (keyof TripLegSearchResponseItem)[]> =
  {
    tripModeName: ['tripModeBC'],
    passengerCareLevelName: ['passengerCareLevelBC'],
    providerName: ['providerName', 'transportationProviderID'],
  };

// What decides whether, and where, a single trip leg appears on the page, as
// of the previous render
type TripLegSnapshot = {
  // Values behind the active sort column(s) as one comparable string
  sortValues: string;
  tripLegStatusBC: string;
  appointmentDate: string;
};

// A trip leg's values behind the active sort column(s), e.g.
// 'appointmentDate,pickUpDueTimeUTC', as one comparable string.
const getSortValues = (
  tripLeg: TripLegSearchResponseItem,
  sortColumns: string[],
): string =>
  sortColumns
    .flatMap(
      (column) =>
        SORT_COLUMN_FIELDS[column] ?? [
          column as keyof TripLegSearchResponseItem,
        ],
    )
    .map((field) => String(tripLeg[field] ?? ''))
    .join('|');

const getTripLegSnapshot = (
  tripLeg: TripLegSearchResponseItem,
  sortColumns: string[],
): TripLegSnapshot => ({
  sortValues: getSortValues(tripLeg, sortColumns),
  tripLegStatusBC: tripLeg.tripLegStatusBC,
  appointmentDate: tripLeg.appointmentDate,
});

const shouldSyncTodaysTripsPage = ({
  events,
  visibleTripLegReferenceIDs,
  tripLegsById,
  previousSnapshots,
  sortColumns,
  tripLegStatusBCs,
  search,
  today,
}: {
  events: Partial<BaseEvent>[];
  // The reference IDs on the page being viewed
  visibleTripLegReferenceIDs: string[];
  // Every loaded trip leg as it is now, with the events already applied
  tripLegsById: Record<string, TripLegSearchResponseItem | undefined>;
  // Every loaded trip leg's snapshot from before the events were applied
  previousSnapshots: Map<string, TripLegSnapshot>;
  sortColumns: string[];
  // The active status filter, if any
  tripLegStatusBCs?: string[];
  // The active search term, if any
  search?: string;
  // yyyy-MM-dd
  today: string;
}): boolean => {
  const matchesStatusFilter = (tripLegStatusBC?: string) =>
    !tripLegStatusBCs?.length ||
    (!!tripLegStatusBC && tripLegStatusBCs.includes(tripLegStatusBC));
  // Whether a trip leg belongs in Today's Trips with the active filter at
  // all, by its appointment date and status. A missing appointment date
  // can't rule it out.
  const isEligible = ({
    appointmentDate,
    tripLegStatusBC,
  }: Pick<TripLegSnapshot, 'appointmentDate' | 'tripLegStatusBC'>) =>
    (!appointmentDate || appointmentDate.startsWith(today)) &&
    matchesStatusFilter(tripLegStatusBC);

  return events.some((event) => {
    const tripLegReferenceID: string | undefined =
      event.detail?.tripLegReferenceID;
    if (!tripLegReferenceID) {
      // e.g. TripLegCountsUpdated, which isn't about a single trip leg
      return false;
    }
    if (search?.trim()) {
      // Which trip legs match a search (by address, member, and so on) is
      // decided by the server, so any trip leg event could change the
      // results. Search results are a narrow set, so syncing on every event
      // while searching stays cheap.
      return true;
    }
    const isVisible = visibleTripLegReferenceIDs.includes(tripLegReferenceID);
    if (EXCEPTION_EVENT_TYPES.includes(event.detailType ?? '')) {
      return isVisible;
    }

    const tripLeg = tripLegsById[tripLegReferenceID];
    if (!tripLeg) {
      // A trip leg that isn't loaded (the store only holds trip legs a fetch
      // loaded in full), so only the event says anything about it
      const status: string | undefined =
        event.detail?.tripLegStatusBC ??
        (event.detail?.status
          ? toTripLegStatusBC(event.detail.status)
          : CREATED_EVENT_STATUS_BCS[event.detailType ?? '']);
      if (CREATED_EVENT_STATUS_BCS[event.detailType ?? '']) {
        // A newly created trip leg, so the server decides where, or whether,
        // it lands on this page
        return isEligible({
          appointmentDate: event.detail?.appointmentDate,
          tripLegStatusBC: status ?? '',
        });
      }
      // With a status filter active, a status change into the filter can
      // add it to this page. Without one, nothing in the event says whether
      // it could, so it's not synced; otherwise every event for any trip leg
      // not yet loaded would refetch this page.
      return !!tripLegStatusBCs?.length && matchesStatusFilter(status);
    }

    const previous = previousSnapshots.get(tripLegReferenceID);
    if (!previous) {
      // Loaded since the previous render, so there's nothing to compare
      // against; the server decides where, or whether, it lands on this page
      return isEligible(tripLeg);
    }

    const hasMoved =
      previous.sortValues !== getSortValues(tripLeg, sortColumns);

    if (isVisible) {
      // The server would drop it from this page (moved to another day or out
      // of the status filter), or move it within or off the page
      return !isEligible(tripLeg) || hasMoved;
    }

    // An off-page trip leg that's now eligible for this page, and either
    // wasn't before (moved to today or into the status filter) or its
    // position could have changed
    return isEligible(tripLeg) && (!isEligible(previous) || hasMoved);
  });
};

export type { TripLegSnapshot };
export { getTripLegSnapshot, shouldSyncTodaysTripsPage };
